import React, { useState, useRef, useCallback } from 'react';
import {
  Upload, Button, message, Progress, Typography, Tag, Table, Space, Tooltip, Badge,
} from 'antd';
import {
  InboxOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  FileTextOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FileImageOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ScanOutlined,
  FileSearchOutlined,
  RobotOutlined,
  EditOutlined,
  ShopOutlined,
  FolderOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  CarOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Dragger } = Upload;
const { Text, Title } = Typography;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ─── Configuration ───
const BATCH_SIZE = 5;         // Files per batch sent to backend
const MAX_PARALLEL_BATCHES = 1; // Send batches sequentially (safest)
const REQUEST_TIMEOUT = 180000; // 2 min per batch (10 files)

// ─── Helpers ───

const getFileIcon = (filename, size = 28) => {
  if (!filename) return <FileTextOutlined style={{ fontSize: size, color: '#8c8c8c' }} />;
  const ext = filename.split('.').pop().toLowerCase();
  if (['pdf'].includes(ext)) return <FilePdfOutlined style={{ fontSize: size, color: '#ff4d4f' }} />;
  if (['xlsx', 'xls', 'csv'].includes(ext)) return <FileExcelOutlined style={{ fontSize: size, color: '#52c41a' }} />;
  if (['doc', 'docx'].includes(ext)) return <FileWordOutlined style={{ fontSize: size, color: '#1a6ed8' }} />;
  if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return <FileImageOutlined style={{ fontSize: size, color: '#faad14' }} />;
  return <FileTextOutlined style={{ fontSize: size, color: '#8c8c8c' }} />;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

const getFileTypeLabel = (filename) => {
  if (!filename) return 'File';
  const ext = filename.split('.').pop().toLowerCase();
  const types = {
    pdf: 'PDF', xlsx: 'Excel', xls: 'Excel', csv: 'CSV',
    doc: 'Word', docx: 'Word', jpg: 'Image', jpeg: 'Image',
    png: 'Image', gif: 'Image', txt: 'Text',
  };
  return types[ext] || ext.toUpperCase();
};

const getConfidenceLabel = (confidence) => {
  if (confidence >= 0.8) return 'High';
  if (confidence >= 0.5) return 'Medium';
  return 'Low';
};

// File status: 'queued' | 'uploading' | 'processing' | 'success' | 'error'
const STATUS_CONFIG = {
  queued: { color: '#8c8c8c', icon: <ClockCircleOutlined />, label: 'Queued' },
  uploading: { color: '#1a6ed8', icon: <LoadingOutlined spin />, label: 'Uploading' },
  processing: { color: '#722ed1', icon: <ScanOutlined spin />, label: 'AI Scanning' },
  success: { color: '#52c41a', icon: <CheckCircleFilled />, label: 'Done' },
  error: { color: '#ff4d4f', icon: <CloseCircleFilled />, label: 'Failed' },
};

// ─── Component ───

const FileUploader = ({ folderPath, folderName, onUploadSuccess }) => {
  const [fileList, setFileList] = useState([]);

  // 'idle' | 'processing' | 'done' | 'error'
  const [stage, setStage] = useState('idle');

  // Per-file status tracking
  const [fileStatuses, setFileStatuses] = useState({});
  // { [uid]: { status, result, error } }

  // Batch progress
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [processedResults, setProcessedResults] = useState([]);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [errorDetail, setErrorDetail] = useState('');
  const [isPaused, setIsPaused] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  const pauseRef = useRef(false);
  const timerRef = useRef(null);

  // ─── Timer ───
  const startTimer = () => {
    const start = Date.now();
    setStartTime(start);
    timerRef.current = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - start) / 1000));
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  // ─── Reset ───
  const resetAll = () => {
    setFileList([]);
    setStage('idle');
    setFileStatuses({});
    setCurrentBatch(0);
    setTotalBatches(0);
    setProcessedResults([]);
    setUploadSummary(null);
    setErrorDetail('');
    setIsPaused(false);
    setElapsedTime(0);
    stopTimer();
    pauseRef.current = false;
  };

  const removeFile = (uid) => {
    setFileList((prev) => prev.filter((f) => f.uid !== uid));
  };

  // ─── Update single file status ───
  const updateFileStatus = useCallback((uid, status, data = {}) => {
    setFileStatuses((prev) => ({
      ...prev,
      [uid]: { ...prev[uid], status, ...data },
    }));
  }, []);

  // ─── Chunk array helper ───
  const chunkArray = (arr, size) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  // ─── Send a single batch to backend ───
  const sendBatch = async (batchFiles, batchIndex, totalBatchCount) => {
    const formData = new FormData();
    batchFiles.forEach((file) => {
      formData.append('files', file.originFileObj || file);
    });
    formData.append('folder_path', folderPath);
    formData.append('batch_index', batchIndex.toString());
    formData.append('total_batches', totalBatchCount.toString());

    // Mark files as processing
    batchFiles.forEach((file) => {
      updateFileStatus(file.uid, 'processing');
    });

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/upload-and-process-batch`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: REQUEST_TIMEOUT,
        }
      );

      const data = response.data;
      const results = data.results || [];

      // Update individual file statuses
      batchFiles.forEach((file, i) => {
        const result = results[i];
        if (result) {
          updateFileStatus(file.uid, result.status === 'success' ? 'success' : 'error', {
            result,
            error: result.status === 'error' ? result.message : null,
          });
        }
      });

      return { success: true, results, data };
    } catch (error) {
      // Mark all files in batch as error
      const errorMsg = error.code === 'ECONNABORTED'
        ? 'Batch timed out'
        : error.response?.data?.message || error.message || 'Batch failed';

      batchFiles.forEach((file) => {
        updateFileStatus(file.uid, 'error', { error: errorMsg });
      });

      return {
        success: false,
        results: batchFiles.map((f) => ({
          original_filename: f.name,
          new_filename: null,
          invoice_number: null,
          confidence: 0,
          method: 'none',
          supplier_name: null,
          vehicle_number: null,
          folder_number: '',
          status: 'error',
          message: errorMsg,
          s3_key: null,
        })),
      };
    }
  };

  // ═══════════════════════════════════════════
  //  MAIN: BATCHED UPLOAD & PROCESS
  // ═══════════════════════════════════════════

  const handleUploadAndProcess = async () => {
    if (fileList.length === 0) {
      message.warning('Please select files to upload');
      return;
    }

    setStage('processing');
    setErrorDetail('');
    setProcessedResults([]);
    setIsPaused(false);
    pauseRef.current = false;

    // Initialize all file statuses as 'queued'
    const initialStatuses = {};
    fileList.forEach((f) => {
      initialStatuses[f.uid] = { status: 'queued', result: null, error: null };
    });
    setFileStatuses(initialStatuses);

    // Split into batches
    const batches = chunkArray(fileList, BATCH_SIZE);
    setTotalBatches(batches.length);
    setCurrentBatch(0);

    startTimer();

    const allResults = [];
    let totalErrors = 0;

    for (let i = 0; i < batches.length; i++) {
      // Check for pause
      while (pauseRef.current) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      setCurrentBatch(i + 1);

      // Mark upcoming batch files as 'uploading'
      batches[i].forEach((file) => {
        updateFileStatus(file.uid, 'uploading');
      });

      const batchResult = await sendBatch(batches[i], i, batches.length);
      allResults.push(...batchResult.results);

      if (!batchResult.success) {
        totalErrors += batches[i].length;
      }

      // Small delay between batches to prevent overwhelming
      if (i < batches.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }

    stopTimer();

    // ── Calculate summary ──
    const total = allResults.length;
    const successCount = allResults.filter((r) => r.status === 'success').length;
    const errorCount = allResults.filter((r) => r.status === 'error').length;
    const detectedCount = allResults.filter((r) => r.invoice_number).length;
    const vehiclesCount = allResults.filter((r) => r.vehicle_number).length;

    setProcessedResults(allResults);
    setUploadSummary({
      total,
      uploaded: successCount,
      errors: errorCount,
      detected: detectedCount,
      vehicles: vehiclesCount,
      folderNumber: allResults[0]?.folder_number || '',
    });

    if (errorCount === total) {
      setStage('error');
      setErrorDetail(`All ${total} files failed. Check server connection.`);
    } else {
      setStage('done');
    }

    // Notify parent of successful uploads
    allResults.forEach((r) => {
      if (r.status === 'success') {
        onUploadSuccess({
          key: r.s3_key,
          original_filename: r.original_filename,
          renamed_filename: r.new_filename,
          invoice_number: r.invoice_number,
          supplier_name: r.supplier_name,
          vehicle_number: r.vehicle_number,
          folder_number: r.folder_number,
        });
      }
    });
  };

  // ─── Retry failed files only ───
  const handleRetryFailed = async () => {
    const failedFiles = fileList.filter(
      (f) => fileStatuses[f.uid]?.status === 'error'
    );

    if (failedFiles.length === 0) {
      message.info('No failed files to retry');
      return;
    }

    setStage('processing');
    message.info(`Retrying ${failedFiles.length} failed file(s)...`);

    // Reset failed statuses to queued
    failedFiles.forEach((f) => {
      updateFileStatus(f.uid, 'queued', { error: null });
    });

    const batches = chunkArray(failedFiles, BATCH_SIZE);
    const retryResults = [];

    for (let i = 0; i < batches.length; i++) {
      batches[i].forEach((f) => updateFileStatus(f.uid, 'uploading'));
      const result = await sendBatch(batches[i], i, batches.length);
      retryResults.push(...result.results);
    }

    // Merge retry results into processedResults
    setProcessedResults((prev) => {
      const updated = [...prev];
      retryResults.forEach((retryResult) => {
        const idx = updated.findIndex(
          (r) => r.original_filename === retryResult.original_filename
        );
        if (idx >= 0) {
          updated[idx] = retryResult;
        } else {
          updated.push(retryResult);
        }
      });
      return updated;
    });

    // Recalculate summary
    setProcessedResults((prev) => {
      const total = prev.length;
      const successCount = prev.filter((r) => r.status === 'success').length;
      const errorCount = prev.filter((r) => r.status === 'error').length;
      const detectedCount = prev.filter((r) => r.invoice_number).length;
      const vehiclesCount = prev.filter((r) => r.vehicle_number).length;

      setUploadSummary({
        total,
        uploaded: successCount,
        errors: errorCount,
        detected: detectedCount,
        vehicles: vehiclesCount,
      });

      return prev;
    });

    setStage('done');
  };

  // ─── Pause/Resume ───
  const togglePause = () => {
    pauseRef.current = !pauseRef.current;
    setIsPaused(!isPaused);
  };

  // ─── Upload Props ───
  const uploadProps = {
    name: 'files',
    multiple: true,
    fileList: [],
    showUploadList: false,
    beforeUpload: (file) => {
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        message.error(`"${file.name}" is too large (${formatFileSize(file.size)}). Max: 50 MB.`);
        return Upload.LIST_IGNORE;
      }
      const allowedExtensions = ['pdf', 'xlsx', 'xls', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'txt', 'csv'];
      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        message.error(`".${ext}" is not supported.`);
        return Upload.LIST_IGNORE;
      }
      setFileList((prev) => {
        if (prev.some((f) => f.name === file.name)) {
          message.warning(`"${file.name}" already added.`);
          return prev;
        }
        return [...prev, file];
      });
      return false;
    },
    accept: '.pdf,.xlsx,.xls,.doc,.docx,.jpg,.jpeg,.png,.txt,.csv',
  };

  // ─── Results Table Columns ───
  const resultColumns = [
    {
      title: 'Original File',
      dataIndex: 'original_filename',
      key: 'original',
      width: 180,
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {getFileIcon(text, 16)}
          <Text style={{ fontSize: 11, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#8dabc4' }}>
            {text}
          </Text>
        </div>
      ),
    },
    {
      title: 'Renamed To',
      dataIndex: 'new_filename',
      key: 'renamed',
      render: (text) => text ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <EditOutlined style={{ color: '#1a6ed8', fontSize: 12 }} />
          <Text strong style={{ fontSize: 12, color: '#1a3353' }}>{text}</Text>
        </div>
      ) : <Text style={{ fontSize: 11, color: '#bfbfbf' }}>—</Text>,
    },
    {
      title: 'Invoice #',
      dataIndex: 'invoice_number',
      key: 'invoice',
      width: 130,
      render: (text) =>
        text ? (
          <Tag color="blue" style={{ borderRadius: 4, fontSize: 11 }}>{text}</Tag>
        ) : (
          <Tag color="default" style={{ borderRadius: 4, fontSize: 11 }}>Not Detected</Tag>
        ),
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier_name',
      key: 'supplier',
      width: 160,
      render: (text) =>
        text ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <ShopOutlined style={{ color: '#722ed1', fontSize: 12 }} />
            <Text style={{ fontSize: 11, color: '#434343', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {text}
            </Text>
          </div>
        ) : (
          <Text style={{ fontSize: 11, color: '#bfbfbf' }}>Unknown</Text>
        ),
    },
    {
      title: 'Vehicle No.',
      dataIndex: 'vehicle_number',
      key: 'vehicle',
      width: 110,
      render: (text) =>
        text ? (
          <Tag icon={<CarOutlined />} color="geekblue" style={{ borderRadius: 4, fontSize: 11 }}>
            {text}
          </Tag>
        ) : (
          <Text style={{ fontSize: 11, color: '#bfbfbf' }}>—</Text>
        ),
    },
    {
      title: 'Folder',
      dataIndex: 'folder_number',
      key: 'folder',
      width: 70,
      render: (text) => (
        <Tag icon={<FolderOutlined />} style={{ borderRadius: 4, fontSize: 11, background: '#f0f7ff', border: '1px solid #d6e4f0', color: '#1a6ed8' }}>
          {text || folderName}
        </Tag>
      ),
    },
    {
      title: 'Confidence',
      dataIndex: 'confidence',
      key: 'confidence',
      width: 90,
      render: (val) =>
        val > 0 ? (
          <Tooltip title={`${(val * 100).toFixed(1)}%`}>
            <Tag
              color={val >= 0.8 ? 'success' : val >= 0.5 ? 'warning' : 'error'}
              style={{ borderRadius: 4, fontSize: 10 }}
            >
              {getConfidenceLabel(val)} {(val * 100).toFixed(0)}%
            </Tag>
          </Tooltip>
        ) : (
          <Text style={{ fontSize: 11, color: '#bfbfbf' }}>—</Text>
        ),
    },
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      width: 95,
      render: (text) => {
        const m = {
          invoice_parser: { label: 'AI Parser', color: '#1a6ed8' },
          ocr_regex: { label: 'OCR+Regex', color: '#722ed1' },
          none: { label: 'None', color: '#8c8c8c' },
        }[text] || { label: text, color: '#8c8c8c' };
        return (
          <Tag style={{ borderRadius: 4, fontSize: 10, color: m.color, background: `${m.color}10`, border: `1px solid ${m.color}30` }}>
            {m.label}
          </Tag>
        );
      },
    },
    {
      title: 'Time',
      dataIndex: 'processing_time',
      key: 'time',
      width: 60,
      render: (val) => val ? (
        <Text style={{ fontSize: 10, color: '#8dabc4' }}>{val}s</Text>
      ) : null,
    },
    {
      title: '',
      dataIndex: 'status',
      key: 'status',
      width: 40,
      render: (status) =>
        status === 'success' ? (
          <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />
        ) : (
          <Tooltip title="Failed">
            <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 16 }} />
          </Tooltip>
        ),
    },
  ];

  // ─── Calculate live progress stats ───
  const getProgressStats = () => {
    const statuses = Object.values(fileStatuses);
    const total = statuses.length;
    if (total === 0) return { percent: 0, done: 0, processing: 0, queued: 0, errors: 0 };

    const done = statuses.filter((s) => s.status === 'success').length;
    const errors = statuses.filter((s) => s.status === 'error').length;
    const processing = statuses.filter((s) => ['uploading', 'processing'].includes(s.status)).length;
    const queued = statuses.filter((s) => s.status === 'queued').length;

    return {
      percent: Math.round(((done + errors) / total) * 100),
      done,
      processing,
      queued,
      errors,
      total,
    };
  };

  const progressStats = getProgressStats();

  // ═══════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════

  return (
    <div>
      {/* ── PROCESSING STATE ── */}
      {stage === 'processing' && (
        <div style={{ background: '#f8faff', border: '1px solid #d6e4f0', borderRadius: 12, padding: 28 }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ width: 64, height: 64, borderRadius: 16, background: 'linear-gradient(135deg, #eef4ff, #d6e4f0)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
              <ThunderboltOutlined style={{ fontSize: 30, color: '#1a6ed8' }} />
            </div>
            <Title level={5} style={{ margin: '0 0 4px', color: '#1a3353' }}>
              Processing Documents
            </Title>
            <Text style={{ color: '#5a6d80', fontSize: 13 }}>
              Batch {currentBatch} of {totalBatches} — {BATCH_SIZE} files per batch
              {isPaused && <Tag color="warning" style={{ marginLeft: 8 }}>PAUSED</Tag>}
            </Text>
          </div>

          {/* Main Progress Bar */}
          <Progress
            percent={progressStats.percent}
            strokeColor={{ '0%': '#4a9eff', '100%': '#1a6ed8' }}
            trailColor="#e8ecf1"
            strokeWidth={10}
            style={{ maxWidth: 450, margin: '0 auto 20px' }}
            format={(p) => `${p}%`}
          />

          {/* Live Stats */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
            <div style={{ textAlign: 'center', padding: '8px 14px', background: '#fff', borderRadius: 8, border: '1px solid #d9f7be', minWidth: 70 }}>
              <Text style={{ fontSize: 10, color: '#8dabc4', display: 'block' }}>Done</Text>
              <Text strong style={{ fontSize: 20, color: '#52c41a' }}>{progressStats.done}</Text>
            </div>
            <div style={{ textAlign: 'center', padding: '8px 14px', background: '#fff', borderRadius: 8, border: '1px solid #d6e4f0', minWidth: 70 }}>
              <Text style={{ fontSize: 10, color: '#8dabc4', display: 'block' }}>Processing</Text>
              <Text strong style={{ fontSize: 20, color: '#1a6ed8' }}>{progressStats.processing}</Text>
            </div>
            <div style={{ textAlign: 'center', padding: '8px 14px', background: '#fff', borderRadius: 8, border: '1px solid #e8ecf1', minWidth: 70 }}>
              <Text style={{ fontSize: 10, color: '#8dabc4', display: 'block' }}>Queued</Text>
              <Text strong style={{ fontSize: 20, color: '#8c8c8c' }}>{progressStats.queued}</Text>
            </div>
            {progressStats.errors > 0 && (
              <div style={{ textAlign: 'center', padding: '8px 14px', background: '#fff', borderRadius: 8, border: '1px solid #ffa39e', minWidth: 70 }}>
                <Text style={{ fontSize: 10, color: '#8dabc4', display: 'block' }}>Failed</Text>
                <Text strong style={{ fontSize: 20, color: '#ff4d4f' }}>{progressStats.errors}</Text>
              </div>
            )}
            <div style={{ textAlign: 'center', padding: '8px 14px', background: '#fff', borderRadius: 8, border: '1px solid #e8ecf1', minWidth: 70 }}>
              <Text style={{ fontSize: 10, color: '#8dabc4', display: 'block' }}>Time</Text>
              <Text strong style={{ fontSize: 16, color: '#1a3353' }}>{formatTime(elapsedTime)}</Text>
            </div>
          </div>

          {/* Per-file status list */}
          <div style={{ maxHeight: 250, overflowY: 'auto', border: '1px solid #e8ecf1', borderRadius: 10, background: '#fff' }}>
            {fileList.map((file, index) => {
              const fs = fileStatuses[file.uid] || { status: 'queued' };
              const cfg = STATUS_CONFIG[fs.status] || STATUS_CONFIG.queued;
              return (
                <div
                  key={file.uid || index}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 14px',
                    borderBottom: index < fileList.length - 1 ? '1px solid #f5f5f5' : 'none',
                    background: fs.status === 'processing' ? '#f0f7ff' : fs.status === 'error' ? '#fff2f0' : 'transparent',
                  }}
                >
                  {getFileIcon(file.name, 16)}
                  <Text style={{
                    flex: 1, fontSize: 12, color: '#434343',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {file.name}
                  </Text>
                  <Tag
                    icon={cfg.icon}
                    style={{
                      borderRadius: 4, fontSize: 10,
                      color: cfg.color, background: `${cfg.color}10`,
                      border: `1px solid ${cfg.color}30`,
                    }}
                  >
                    {cfg.label}
                  </Tag>
                </div>
              );
            })}
          </div>

          {/* Pause/Resume */}
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button
              icon={isPaused ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
              onClick={togglePause}
              style={{ borderRadius: 8 }}
            >
              {isPaused ? 'Resume' : 'Pause'}
            </Button>
          </div>
        </div>
      )}

      {/* ── DONE STATE — Results ── */}
      {stage === 'done' && uploadSummary && (
        <div>
          {/* Summary */}
          <div style={{ background: 'linear-gradient(135deg, #f6ffed 0%, #eeffdb 100%)', border: '1px solid #b7eb8f', borderRadius: 12, padding: '20px 24px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <CheckCircleFilled style={{ fontSize: 36, color: '#52c41a' }} />
            <div style={{ flex: 1, minWidth: 200 }}>
              <Title level={5} style={{ margin: 0, color: '#237804' }}>Processing Complete!</Title>
              <Text style={{ color: '#389e0d', fontSize: 13 }}>
                {uploadSummary.uploaded} of {uploadSummary.total} files uploaded to Folder {folderName}
                {elapsedTime > 0 && ` in ${formatTime(elapsedTime)}`}
              </Text>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center', padding: '8px 16px', background: '#fff', borderRadius: 8, border: '1px solid #d9f7be' }}>
                <Text style={{ fontSize: 10, color: '#8dabc4', display: 'block' }}>Invoices Found</Text>
                <Text strong style={{ fontSize: 20, color: '#52c41a' }}>{uploadSummary.detected}</Text>
              </div>
              {/* v6: Vehicle Numbers detected */}
              {uploadSummary.vehicles > 0 && (
                <div style={{ textAlign: 'center', padding: '8px 16px', background: '#fff', borderRadius: 8, border: '1px solid #adc6ff' }}>
                  <Text style={{ fontSize: 10, color: '#8dabc4', display: 'block' }}>Vehicles</Text>
                  <Text strong style={{ fontSize: 20, color: '#2f54eb' }}>{uploadSummary.vehicles}</Text>
                </div>
              )}
              <div style={{ textAlign: 'center', padding: '8px 16px', background: '#fff', borderRadius: 8, border: '1px solid #ffe58f' }}>
                <Text style={{ fontSize: 10, color: '#8dabc4', display: 'block' }}>Undetected</Text>
                <Text strong style={{ fontSize: 20, color: '#faad14' }}>{uploadSummary.total - uploadSummary.detected}</Text>
              </div>
              {uploadSummary.errors > 0 && (
                <div style={{ textAlign: 'center', padding: '8px 16px', background: '#fff', borderRadius: 8, border: '1px solid #ffa39e' }}>
                  <Text style={{ fontSize: 10, color: '#8dabc4', display: 'block' }}>Errors</Text>
                  <Text strong style={{ fontSize: 20, color: '#ff4d4f' }}>{uploadSummary.errors}</Text>
                </div>
              )}
            </div>
          </div>

          {/* Retry failed button */}
          {uploadSummary.errors > 0 && (
            <Button
              type="primary" danger icon={<ReloadOutlined />}
              onClick={handleRetryFailed}
              style={{ marginBottom: 16, borderRadius: 8 }}
            >
              Retry {uploadSummary.errors} Failed File{uploadSummary.errors > 1 ? 's' : ''}
            </Button>
          )}

          {/* Naming format info */}
          <div style={{ background: '#f9f0ff', border: '1px solid #d3adf7', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#531dab' }}>
            <strong>Naming format:</strong> InvoiceNo_SupplierName_Folder{folderName}.ext
          </div>

          {/* Results Table */}
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf1', overflow: 'hidden', marginBottom: 20 }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf1', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FileSearchOutlined style={{ color: '#1a6ed8', fontSize: 16 }} />
              <Text strong style={{ color: '#1a3353', fontSize: 14 }}>Processing Results</Text>
              <Tag color="processing" style={{ marginLeft: 'auto', borderRadius: 4 }}>{processedResults.length} files</Tag>
            </div>
            <Table
              columns={resultColumns}
              dataSource={processedResults.map((r, i) => ({ ...r, key: i }))}
              pagination={processedResults.length > 20 ? { pageSize: 20, size: 'small' } : false}
              size="small"
              scroll={{ x: 1100 }}
            />
          </div>

          <Button type="primary" icon={<CloudUploadOutlined />} onClick={resetAll} size="large"
            style={{ borderRadius: 10, height: 48, fontWeight: 600, background: '#52c41a', borderColor: '#52c41a' }}>
            Upload More Files
          </Button>
        </div>
      )}

      {/* ── ERROR STATE ── */}
      {stage === 'error' && (
        <div style={{ background: 'linear-gradient(135deg, #fff2f0 0%, #ffeded 100%)', border: '1px solid #ffccc7', borderRadius: 12, padding: 24, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <CloseCircleFilled style={{ fontSize: 32, color: '#ff4d4f', marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <Title level={5} style={{ margin: '0 0 4px', color: '#a8071a' }}>Processing Failed</Title>
              <Text style={{ color: '#cf1322', fontSize: 13 }}>{errorDetail}</Text>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <Button type="primary" danger icon={<ReloadOutlined />} onClick={handleUploadAndProcess}>Retry All</Button>
                <Button icon={<DeleteOutlined />} onClick={resetAll}>Start Over</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── IDLE STATE ── */}
      {stage === 'idle' && (
        <>
          {/* AI info banner */}
          <div style={{ background: 'linear-gradient(135deg, #f0f7ff, #e8f0fe)', border: '1px solid #d6e4f0', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <RobotOutlined style={{ fontSize: 20, color: '#1a6ed8' }} />
            <div>
              <Text strong style={{ fontSize: 13, color: '#1a3353' }}>AI-Powered Document Processing</Text>
              <Text style={{ fontSize: 12, color: '#5a6d80', display: 'block' }}>
                Files are scanned to extract invoice numbers, supplier names &amp; vehicle/plate numbers, then renamed as: <strong>InvoiceNo_Supplier_Folder{folderName}.pdf</strong>
              </Text>
              <Text style={{ fontSize: 11, color: '#8dabc4', display: 'block', marginTop: 2 }}>
                <ThunderboltOutlined /> Processes {BATCH_SIZE} files in parallel per batch — handles 100+ files smoothly
              </Text>
            </div>
          </div>

          {/* Drag & Drop */}
          <Dragger {...uploadProps} style={{ padding: '24px', background: fileList.length > 0 ? '#f8faff' : '#fafbfd', borderColor: fileList.length > 0 ? '#1a6ed8' : '#d9d9d9', borderRadius: 12, borderStyle: 'dashed', borderWidth: 2 }}>
            <p style={{ marginBottom: 12 }}><InboxOutlined style={{ fontSize: 48, color: '#4a9eff' }} /></p>
            <p style={{ fontSize: 16, color: '#1a3353', fontWeight: 600, marginBottom: 6 }}>Click or drag files to upload</p>
            <p style={{ color: '#8dabc4', fontSize: 13, marginBottom: 4 }}>Upload multiple PDFs, images, or documents at once</p>
            <p style={{ color: '#b0bec5', fontSize: 12 }}>PDF &amp; images will be auto-scanned for invoice numbers &amp; vehicle plates &middot; Max 50 MB per file</p>
          </Dragger>

          {/* Selected Files List */}
          {fileList.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <Text strong style={{ fontSize: 14, color: '#1a3353' }}>
                  Selected Files ({fileList.length})
                  {fileList.length > BATCH_SIZE && (
                    <Text style={{ fontSize: 12, color: '#8dabc4', fontWeight: 'normal', marginLeft: 8 }}>
                      Will process in {Math.ceil(fileList.length / BATCH_SIZE)} batches
                    </Text>
                  )}
                </Text>
                <Button type="link" danger size="small" onClick={() => setFileList([])}>Clear All</Button>
              </div>

              <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #e8ecf1', borderRadius: 10 }}>
                {fileList.map((file, index) => (
                  <div
                    key={file.uid || index}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: index < fileList.length - 1 ? '1px solid #f0f3f6' : 'none', transition: 'background 0.15s' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    {getFileIcon(file.name, 20)}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{ fontSize: 13, color: '#1a3353', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</Text>
                      <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                        <Tag style={{ fontSize: 10, borderRadius: 4, margin: 0, background: '#f0f7ff', border: '1px solid #d6e4f0', color: '#1a6ed8' }}>{getFileTypeLabel(file.name)}</Tag>
                        <Text style={{ fontSize: 11, color: '#8dabc4' }}>{formatFileSize(file.size)}</Text>
                      </div>
                    </div>
                    <Button type="text" size="small" icon={<DeleteOutlined style={{ color: '#ff4d4f', fontSize: 14 }} />} onClick={() => removeFile(file.uid)} style={{ width: 30, height: 30 }} />
                  </div>
                ))}
              </div>

              <Button type="primary" size="large" icon={<ScanOutlined />} onClick={handleUploadAndProcess}
                style={{ marginTop: 16, width: '100%', borderRadius: 10, height: 52, fontWeight: 600, fontSize: 15, background: 'linear-gradient(135deg, #1a6ed8, #4a9eff)', border: 'none', boxShadow: '0 4px 12px rgba(26,110,216,0.3)' }}>
                Process &amp; Upload {fileList.length} File{fileList.length > 1 ? 's' : ''} to Folder {folderName}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FileUploader;